import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/for-venues")({
  head: () => ({
    meta: [
      { title: "For venues — Fill bar shifts with vetted bartenders | The Hospitality Reserve" },
      { name: "description", content: "Post a shift in minutes, get applications from vetted, insured bartenders, and only pay a flat platform fee. Built for UK bars, hotels and restaurants." },
      { property: "og:title", content: "For venues — The Hospitality Reserve" },
      { property: "og:description", content: "Vetted bartenders. Transparent flat fees. No agency mark-up." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thehospitalityreserve.com/for-venues" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/for-venues" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "How quickly can I fill a shift?", acceptedAnswer: { "@type": "Answer", text: "Most shifts posted 48+ hours ahead attract applications within the first few hours." } },
          { "@type": "Question", name: "How much does it cost?", acceptedAnswer: { "@type": "Answer", text: "A flat £5 platform fee per booked staff member. No agency mark-up on the bartender's pay." } },
          { "@type": "Question", name: "Are bartenders insured?", acceptedAnswer: { "@type": "Answer", text: "Yes — every bartender confirms they hold Public Liability Insurance before accepting a shift." } },
        ],
      }),
    }],
  }),
  component: ForVenuesPage,
});

function ForVenuesPage() {
  return (
    <main className="mx-auto max-w-4xl space-y-16 p-6 py-16">
      <header className="space-y-4">
        <p className="text-[0.6rem] tracking-wide-luxury uppercase text-accent">For venues</p>
        <h1 className="text-4xl font-medium md:text-5xl">Bar staff, on demand — without the agency mark-up.</h1>
        <p className="text-lg text-muted-foreground">
          Post a shift in minutes. Applications from vetted, insured bartenders arrive within hours. Pay a flat platform fee and pocket the difference.
        </p>
        <div className="flex gap-3">
          <Link to="/auth" search={{ mode: "signup", role: "venue" } as never} className="border border-accent bg-accent px-5 py-3 text-sm uppercase tracking-wide-luxury text-accent-foreground">Post your first shift</Link>
          <Link to="/leaderboard" className="border border-border px-5 py-3 text-sm uppercase tracking-wide-luxury">See the leaderboard</Link>
        </div>
      </header>

      <section className="grid gap-6 md:grid-cols-3">
        {[
          { h: "Transparent flat fee", p: "£5 per booked staff member, whatever the size. No hidden mark-ups on the bartender's pay." },
          { h: "Vetted & insured", p: "Automated CV experience verification, RTW checks and mandatory Public Liability Insurance." },
          { h: "Instant refunds on no-shows", p: "Report a no-show once 25% of the shift (or 30 minutes, whichever is shorter) has passed — you get a full refund plus 3 free £5 booking credits." },
        ].map((c) => (
          <div key={c.h} className="border border-border p-6">
            <h2 className="text-sm uppercase tracking-wide-luxury text-accent">{c.h}</h2>
            <p className="mt-3 text-sm text-muted-foreground">{c.p}</p>
          </div>
        ))}
      </section>

      <section className="border border-border p-8 space-y-6">
        <h2 className="text-2xl font-medium">How it works</h2>
        <ol className="space-y-4 text-sm">
          <li><strong>1. Post a shift.</strong> Time, rate, role, DBS requirement — 60 seconds.</li>
          <li><strong>2. Review applicants.</strong> See ratings, distance, certifications, and message them before you accept.</li>
          <li><strong>3. Pay only when confirmed.</strong> Card charged on acceptance, released to the bartender after completion.</li>
        </ol>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-medium">Common questions</h2>
        {[
          ["How quickly can I fill a shift?", "Most shifts posted 48+ hours ahead attract applications within the first few hours."],
          ["How much does it cost?", "A flat £5 platform fee per booked staff member. No agency mark-up on the bartender's pay."],
          ["Are bartenders insured?", "Yes — every bartender confirms they hold Public Liability Insurance before accepting a shift."],
        ].map(([q, a]) => (
          <details key={q} className="border border-border p-5">
            <summary className="cursor-pointer text-sm font-medium">{q}</summary>
            <p className="mt-3 text-sm text-muted-foreground">{a}</p>
          </details>
        ))}
      </section>
    </main>
  );
}
